import React, { useState } from 'react';
import {
  runAxeAudit,
  calculateAccessibilityScore,
  logAccessibilityResults,
  downloadAccessibilityReport
} from '../utils/accessibilityTesting';

/**
 * Accessibility Testing Panel
 * Development tool for running accessibility audits
 * Only visible in development mode
 */
const AccessibilityPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [score, setScore] = useState(null);

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const runAudit = async () => {
    setIsRunning(true);
    try {
      const auditResults = await runAxeAudit();
      setResults(auditResults);
      const auditScore = calculateAccessibilityScore(auditResults);
      setScore(auditScore);
      logAccessibilityResults(auditResults);
    } catch (error) {
      console.error('Error running audit:', error);
    }
    setIsRunning(false);
  };

  const handleDownloadReport = () => {
    if (results) {
      downloadAccessibilityReport(results);
    }
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
          backgroundColor: '#0066CC',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          fontSize: '24px',
          cursor: 'pointer',
          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
        }}
        aria-label="Toggle accessibility testing panel"
        title="Accessibility Testing Panel"
      >
        ♿
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '90px',
            right: '20px',
            width: '400px',
            maxHeight: '600px',
            backgroundColor: 'white',
            border: '2px solid #0066CC',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            zIndex: 9998,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px',
              backgroundColor: '#0066CC',
              color: 'white',
              borderRadius: '6px 6px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h3 style={{ margin: 0, fontSize: '18px' }}>♿ A11y Testing</h3>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '0',
                width: '30px',
                height: '30px',
              }}
              aria-label="Close panel"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div
            style={{
              padding: '16px',
              overflowY: 'auto',
              flex: 1,
            }}
          >
            <button
              onClick={runAudit}
              disabled={isRunning}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#006600',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: isRunning ? 'not-allowed' : 'pointer',
                marginBottom: '16px',
              }}
            >
              {isRunning ? '🔄 Running Audit...' : '▶️ Run Accessibility Audit'}
            </button>

            {score && (
              <div
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '6px',
                  marginBottom: '16px',
                }}
              >
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>
                  📊 Score: {score.score}% (Grade: {score.grade})
                </h4>

                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#006600', fontWeight: 'bold' }}>
                    ✅ {score.passedTests} Passed
                  </span>
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#A30000', fontWeight: 'bold' }}>
                    ❌ {score.failedTests} Failed
                  </span>
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#8F6B00', fontWeight: 'bold' }}>
                    ⚠️ {score.incompleteTests} Incomplete
                  </span>
                </div>

                {score.violationsBySeverity && (
                  <div style={{ marginTop: '12px', fontSize: '14px' }}>
                    <strong>By Severity:</strong>
                    <div>Critical: {score.violationsBySeverity.critical || 0}</div>
                    <div>Serious: {score.violationsBySeverity.serious || 0}</div>
                    <div>Moderate: {score.violationsBySeverity.moderate || 0}</div>
                    <div>Minor: {score.violationsBySeverity.minor || 0}</div>
                  </div>
                )}

                <button
                  onClick={handleDownloadReport}
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    padding: '10px',
                    backgroundColor: '#0066CC',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  📥 Download Report
                </button>
              </div>
            )}

            {results && results.violations.length > 0 && (
              <div>
                <h4 style={{ fontSize: '16px', marginBottom: '8px' }}>
                  🚨 Top Issues:
                </h4>
                {results.violations.slice(0, 3).map((violation, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: '#FFE8E8',
                      padding: '12px',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      borderLeft: `4px solid ${
                        violation.impact === 'critical' ? '#A30000' :
                        violation.impact === 'serious' ? '#D40000' :
                        violation.impact === 'moderate' ? '#FF9800' : '#FFC107'
                      }`,
                    }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                      {violation.impact.toUpperCase()}
                    </div>
                    <div style={{ fontSize: '14px' }}>
                      {violation.help}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {violation.nodes.length} element(s) affected
                    </div>
                  </div>
                ))}
                {results.violations.length > 3 && (
                  <div style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                    ... and {results.violations.length - 3} more issues
                  </div>
                )}
              </div>
            )}

            {results && results.violations.length === 0 && score && (
              <div
                style={{
                  backgroundColor: '#E8F5E8',
                  padding: '16px',
                  borderRadius: '6px',
                  textAlign: 'center',
                  color: '#006600',
                  fontWeight: 'bold',
                }}
              >
                🎉 No violations found!
              </div>
            )}

            <div
              style={{
                marginTop: '16px',
                fontSize: '12px',
                color: '#666',
                borderTop: '1px solid #ddd',
                paddingTop: '12px',
              }}
            >
              <strong>Tips:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Run audit after page loads completely</li>
                <li>Check console for detailed results</li>
                <li>Download HTML report for full details</li>
                <li>Test with screen readers for best results</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityPanel;
